/**
 * Many-way routing as a Switch.
 *
 * WhatsApp nests If three deep at most, and its condition parser refuses `||` or `&&` between two
 * comparisons ("Wrong positioning of operator '||'", probed against the Graph API on 2026-09-18),
 * so a chain of Ifs can route at most three rules plus a default. A Switch on the answer has no
 * such limit, and Meta accepts a Footer inside each case (also probed the same day, contrary to
 * the reference's component list). It has no default case, so it is only safe when every value
 * the field can take is known and the customer must answer: a required RadioButtonsGroup or
 * Dropdown with fixed options. Anything else keeps the If chain and the validator says why.
 */

import { parseSimpleExpression } from './conditionUtils';

const SINGLE_CHOICE = new Set(['RadioButtonsGroup', 'Dropdown']);

/** A routing rule as "this field equals this option id", or null when it is anything else. */
export const equalityRule = (rule) => {
  if (!rule || !rule.nextScreenId) return null;
  if (rule.mode === 'power') {
    const parsed = parseSimpleExpression((rule.expression || '').trim());
    return parsed && parsed.operator === 'eq' ? { field: parsed.field, value: parsed.value, nextScreenId: rule.nextScreenId } : null;
  }
  const field = rule.field ?? rule.fieldName;
  if (!field || rule.operator !== 'eq' || rule.value === undefined || rule.value === null) return null;
  return { field, value: String(rule.value), nextScreenId: rule.nextScreenId };
};

/**
 * The Switch a builder screen's routing compiles to, or null when it cannot be one.
 *
 * @returns {{ field: string, cases: Array<{ value: string, nextScreenId: string|null }> } | null}
 *   one case per option of the field, in option order; `nextScreenId` null means the default.
 */
export const switchRouting = (screen) => {
  const rules = ((screen && screen.routing && screen.routing.rules) || []).map(equalityRule);
  if (rules.length === 0 || rules.some((rule) => !rule)) return null;
  const field = rules[0].field;
  if (rules.some((rule) => rule.field !== field)) return null;
  const component = (screen.components || []).find((c) => c && c.properties && c.properties.name === field);
  if (!component || !SINGLE_CHOICE.has(component.type)) return null;
  const props = component.properties;
  if (props.required !== true || props['data-source-mode'] === 'dynamic' || !Array.isArray(props['data-source'])) return null;
  const options = props['data-source'].map((o) => (typeof o === 'string' ? o : o && o.id)).filter((id) => typeof id === 'string' && id !== '');
  if (options.length === 0) return null;
  const cases = options.map((value) => {
    const hit = rules.find((rule) => rule.value === value);
    return { value, nextScreenId: hit ? hit.nextScreenId : null };
  });
  return { field, cases };
};

/** True when a compiled component list ends in the Switch that `switchRouting` produces. */
export const isRoutingSwitch = (node) => Boolean(node) && node.type === 'Switch' && typeof node.value === 'string'
  && node.cases && typeof node.cases === 'object'
  && Object.values(node.cases).every((branch) => Array.isArray(branch) && branch.length === 1 && branch[0] && branch[0].type === 'Footer');
